<template>
  <div style="text-align:left;">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <ModalPerfilProfesional ref="modalPerfil" :categorias="categorias" />
      <div class="main-container">
        <div v-if="loaded" class="articulo-principal">
          <div class="page-header">
            <div>
              <h2 class="section-title">Profesionales</h2>
              <p class="section-description">
                Revisa y gestiona el equipo de especialistas disponibles para tus propiedades. Haz clic en cualquier profesional para ver su perfil completo.
              </p>
            </div>
            <div class="meta-info" v-if="filteredProfesionales.length">
              <span class="count-chip">{{ filteredProfesionales.length }} profesionales</span>
            </div>
          </div>

          <div class="toolbar">
            <div class="search-input">
              <span class="search-icon" aria-hidden="true">🔍</span>
              <input
                v-model="searchQuery"
                type="search"
                placeholder="Buscar por nombre"
                aria-label="Buscar profesionales"
              />
            </div>
          </div>

          <div v-if="filteredProfesionales.length" class="professional-grid">
            <article
              v-for="profesional in filteredProfesionales"
              :key="profesional.profesional.id"
              class="professional-card"
              role="button"
              tabindex="0"
              @click="openProfesionalModal(profesional)"
              @keydown.enter="openProfesionalModal(profesional)"
            >
              <div class="card-top">
                <div
                  class="avatar"
                  :style="{ 'background-image': `url(${profesional.profesional.foto || defaultAvatar})` }"
                  aria-hidden="true"
                ></div>
                <div class="meta">
                  <div class="name-line">
                    <h3 class="name">
                      {{ profesional.profesional.nombre }}
                      {{ profesional.profesional.apellido }}
                    </h3>
                  </div>
                  <div class="badges">
                    <span class="tag soft" v-if="profesional.profesional.oficio">
                      {{ profesional.profesional.oficio }}
                    </span>
                    <span class="tag" v-if="locationOf(profesional)">
                      {{ locationOf(profesional) }}
                    </span>
                  </div>
                  <div class="stats" aria-label="Datos del profesional">
                    <template v-if="hasRatings(profesional)">
                      <span class="stat">⭐ {{ averageRating(profesional) }}</span>
                      <span class="divider">•</span>
                      <span class="stat">{{ profesional.clasificacion[0] }} reseñas</span>
                    </template>
                    <span v-else class="stat stat-empty">Sin calificaciones</span>
                  </div>
                </div>
                <p class="role">
                  {{ resumenProfesional(profesional) || 'Profesional en Jobbing' }}
                </p>
              </div>

              <footer class="cta-row">
                <button
                  type="button"
                  class="cta"
                  @click.stop="openProfesionalModal(profesional)"
                >
                  Ver perfil completo
                </button>
              </footer>
            </article>
          </div>

          <div v-else class="empty-state">
            <span class="empty-icon" aria-hidden="true">👥</span>
            <h3>No encontramos profesionales</h3>
            <p>Prueba ajustando tu búsqueda o recarga la página para volver a intentarlo.</p>
          </div>
        </div>
        <div v-else class="articulo-principal">
          <div class="loading-state">
            <span class="loading-indicator" aria-hidden="true"></span>
            <span>Cargando profesionales...</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import SideNav from '@/components/SideNav.vue';
import TopNav from '@/components/TopNav.vue';
import ModalPerfilProfesional from '@/components/ModalPerfilProfesional.vue';

export default {
  name: 'ProfesionalesEmpresa',
  components: {
    SideNav,
    TopNav,
    ModalPerfilProfesional,
  },
  data() {
    return {
      loaded: false,
      profesionales: [],
      defaultAvatar: 'https://cdn.icon-icons.com/icons2/1378/PNG/512/avatardefault_92824.png',
      searchQuery: '',
    };
  },
  created() {
    this.initialize_();
  },
  methods: {
    initialize_() {
      this.loaded = false;
      axios
        .get(`${window.hostname}api/property_manager/get_profesionales`)
        .then((response) => {
          this.loaded = true;
          this.profesionales = (response.data.profesionales || []).filter(
            (p) => p && p.profesional && p.profesional.id !== 10,
          );
        })
        .catch((e) => {
          this.loaded = true;
          console.error('Error al cargar...', e);
        });
    },
    openProfesionalModal(profesional) {
      if (!profesional || !profesional.profesional) {
        return;
      }
      const nombreSeo = profesional.profesional.nombre_seo;
      if (!nombreSeo || !this.$refs.modalPerfil) {
        return;
      }
      this.$refs.modalPerfil.openByNombreSeo(nombreSeo);
    },
    hasRatings(p) {
      return Boolean(p && p.clasificacion && p.clasificacion[0] > 0);
    },
    averageRating(p) {
      return this.averageRatingNumber(p).toFixed(1);
    },
    averageRatingNumber(p) {
      if (!this.hasRatings(p)) {
        return 0;
      }
      return p.clasificacion[1] / p.clasificacion[0];
    },
    locationOf(profesional) {
      if (!profesional || !profesional.profesional) {
        return '';
      }
      const data = profesional.profesional;
      const locationPieces = [data.comuna, data.ciudad, data.region, data.pais]
        .map((piece) => (piece ? String(piece).trim() : ''))
        .filter((piece, index, arr) => piece && arr.indexOf(piece) === index);
      return locationPieces.join(', ');
    },
    resumenProfesional(profesional) {
      if (!profesional || !profesional.profesional) {
        return '';
      }
      const data = profesional.profesional;
      const fields = ['descripcion', 'resumen', 'presentacion', 'bio'];
      const copy = fields
        .map((field) => (data[field] ? String(data[field]).trim() : ''))
        .find((value) => value);
      return this.truncate(copy, 160);
    },
    truncate(text, maxLength = 140) {
      if (!text) {
        return '';
      }
      if (text.length <= maxLength) {
        return text;
      }
      return `${text.slice(0, maxLength - 1).trim()}…`;
    },
  },
  computed: {
    ...mapGetters({
      admin_datos: 'admin_datos',
      categorias: 'categorias',
    }),
    filteredProfesionales() {
      if (!this.searchQuery) {
        return this.profesionales;
      }
      const query = this.searchQuery.toLowerCase();
      return this.profesionales.filter((item) => {
        const profesional = item.profesional || {};
        const nombre = `${profesional.nombre || ''} ${profesional.apellido || ''}`.toLowerCase();
        const telefono = String(profesional.telefono || '').toLowerCase();
        const email = String(profesional.email || '').toLowerCase();
        return nombre.includes(query) || telefono.includes(query) || email.includes(query);
      });
    },
  },
  mounted() {
    this.$root.$on('update-profesionales', this.initialize_);
  },
  beforeDestroy() {
    this.$root.$off('update-profesionales', this.initialize_);
  },
};
</script>

<style scoped>
.column-right {
  background-color: #f2f7fb;
  min-height: 133vh;
  padding-top: 80px;
  padding-left: 260px;
  width: 100%;
}
.main-container {
  background-color: #fff;
  margin-top: 10px;
  padding: 32px 32px 48px;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}
.section-title {
  font-weight: 700;
  font-size: 28px;
  margin-bottom: 8px;
}
.section-description {
  margin: 0;
  color: #6b7280;
  max-width: 640px;
}
.meta-info {
  display: flex;
  align-items: center;
}
.count-chip {
  background: #e5edff;
  color: #1d4ed8;
  font-weight: 600;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 0.85rem;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 32px;
}
.search-input {
  position: relative;
  flex: 1 1 320px;
}
.search-input input {
  width: 100%;
  padding: 14px 18px 14px 52px;
  border-radius: 16px;
  border: 1px solid #d0d7e6;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  background-color: #f5f7fb;
}
.search-input input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
  background-color: #fff;
}
.search-icon {
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  color: #4b5563;
  font-size: 1.1rem;
}
.professional-grid {
  display: grid;
  gap: 32px;
  grid-template-columns: repeat(auto-fit, minmax(280px, 360px));
  justify-content: flex-start;
}
.professional-card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 12px 28px rgba(17, 37, 66, 0.08);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 240px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  border: 1px solid #e5ecf7;
  width: 100%;
  max-width: 360px;
  margin: 0;
}
.professional-card:focus,
.professional-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 32px rgba(17, 37, 66, 0.12);
  border-color: rgba(37, 99, 235, 0.2);
  cursor: pointer;
}
.card-top {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: start;
}
.avatar {
  height: 80px;
  width: 80px;
  border-radius: 16px;
  border: 1px solid #e5ecf7;
  background-color: #f8fafc;
  background-size: cover;
  background-position: center;
}
.meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.name-line {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
}
.name {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tag {
  padding: 4px 8px;
  background: #eef4ff;
  color: #243558;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.75rem;
  border: 1px solid #d9e6ff;
  text-transform: capitalize;
}
.tag.soft {
  background: #f4f8ff;
  border-color: #e4edff;
  color: #2c3d66;
}
.role {
  grid-column: 1 / -1;
  margin: 0;
  color: #4a566f;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: left;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}
.stats {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #2c3854;
  font-weight: 700;
  font-size: 0.85rem;
}
.stat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.stat-empty {
  color: #94a3b8;
}
.divider {
  color: #c6cedd;
  font-weight: 800;
}
.cta-row {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  margin-top: auto;
}
.cta {
  border: none;
  background: #0b8be4;
  color: #fff;
  font-weight: 800;
  font-size: 0.9rem;
  padding: 10px 14px;
  border-radius: 12px;
  transition: transform 0.2s ease, background-color 0.2s ease;
}
.cta:hover,
.cta:focus {
  background: #0a76c4;
  transform: translateY(-2px);
}
.cta:focus {
  outline: none;
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  text-align: center;
  color: #6b7280;
}
.empty-icon {
  font-size: 56px;
}
.loading-state {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #2563eb;
  font-weight: 600;
  justify-content: center;
  padding: 48px 0;
}
.loading-indicator {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 3px solid rgba(37, 99, 235, 0.25);
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
@media (max-width: 800px) {
  .column-right {
    padding-left: 0;
  }
  .main-container {
    padding: 20px;
  }
  .professional-card {
    min-height: 0;
  }
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
